import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { authApi } from "@/services/api";
import { useAuthStore, useLangStore } from "@/store/useStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogOut, Loader2, Users2, ChevronRight } from "lucide-react";

export default function Settings() {
  const { t } = useTranslation();
  const { user, setUser, logout } = useAuthStore();
  const { lang, setLang } = useLangStore();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await authApi.updateMe({ name: name.trim(), language: lang });
      setUser(res.data.user);
      toast.success(t("settings.saved"));
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="max-w-xl animate-fade-up space-y-6" data-testid="settings-page">
      <h1 className="text-2xl md:text-3xl font-bold text-[#0D1B2A] tracking-tight">{t("settings.title")}</h1>

      <form onSubmit={save} className="bg-white border border-gray-200 rounded-2xl p-5 md:p-6 space-y-5">
        <h2 className="text-sm font-semibold text-[#0D1B2A]">{t("settings.profile")}</h2>
        <div>
          <Label className="text-sm font-semibold text-[#0D1B2A]">{t("settings.name_label")}</Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-12 mt-2 rounded-xl border-gray-300 focus-visible:ring-[#1A3C6E]"
            data-testid="settings-name-input"
          />
        </div>
        <div>
          <Label className="text-sm font-semibold text-[#0D1B2A]">{t("settings.username_label")}</Label>
          <Input
            value={user?.username || ""}
            disabled
            className="h-12 mt-2 rounded-xl bg-gray-50"
          />
        </div>
        <div>
          <Label className="text-sm font-semibold text-[#0D1B2A] mb-2 block">{t("settings.language_label")}</Label>
          <div className="inline-flex rounded-xl bg-gray-100 p-1">
            <button
              type="button"
              onClick={() => setLang("en")}
              data-testid="settings-lang-en"
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${
                lang === "en" ? "bg-white text-[#1A3C6E] shadow-sm" : "text-[#4B5563]"
              }`}
            >
              {t("settings.english")}
            </button>
            <button
              type="button"
              onClick={() => setLang("te")}
              data-testid="settings-lang-te"
              className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${
                lang === "te" ? "bg-white text-[#1A3C6E] shadow-sm" : "text-[#4B5563]"
              }`}
            >
              {t("settings.telugu")}
            </button>
          </div>
        </div>
        <Button
          type="submit"
          disabled={saving}
          className="h-12 rounded-xl bg-[#1A3C6E] hover:bg-[#122A4D] text-white font-semibold px-6 w-full sm:w-auto"
          data-testid="settings-save-btn"
        >
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : t("settings.save")}
        </Button>
      </form>

      {user?.role === "admin" && (
        <button
          onClick={() => navigate("/admin/users")}
          data-testid="settings-admin-users-link"
          className="w-full flex items-center justify-between gap-3 px-5 py-4 rounded-2xl bg-white border border-gray-200 hover:border-[#1A3C6E] hover:shadow-sm transition-all text-left"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-[#F0E8F6] grid place-items-center text-[#6B3FA0]">
              <Users2 className="h-5 w-5" />
            </div>
            <div>
              <div className="font-semibold text-[#0D1B2A]">{t("settings.manage_users")}</div>
              <div className="text-xs text-[#6B7280]">{t("settings.manage_users_desc")}</div>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-[#6B7280]" />
        </button>
      )}

      <button
        onClick={handleLogout}
        data-testid="settings-logout-btn"
        className="w-full inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl border border-red-200 text-[#EF4444] hover:bg-red-50 font-semibold"
      >
        <LogOut className="h-4 w-4" /> {t("settings.logout")}
      </button>
    </div>
  );
}
